import { z } from "zod";

/** Strong password: 8+ chars, upper, lower, number, and special character. */
export const strongPasswordSchema = z
    .string("Password is required")
    .min(8, "Password must be at least 8 characters")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[0-9]/, "Password must contain at least one number")
    .regex(
        /[^A-Za-z0-9]/,
        "Password must contain at least one special character",
    );

export const loginSchema = z.object({
    email: z.string().email("Please enter a valid email address"),
    password: z.string().min(8, "Password must be at least 8 characters"),
});

export type LoginFormData = z.infer<typeof loginSchema>;

export const registerSchema = z
    .object({
        first_name: z.string("First name is required").min(1, "First name is required"),
        middle_name: z.string("Middle name is required").optional(),
        last_name: z.string("Last name is required").min(1, "Last name is required"),
        email: z.string("Email is required").email("Please enter a valid email address"),
        phone: z.string("Phone number is required").min(11, "Phone number must be at least 11 characters"),
        password: strongPasswordSchema,
        password_confirmation: z.string("Password confirmation is required"),
        role: z.enum(["customer", "business_customer", "driver"]),
        address: z.string("Address is required").min(1, "Address is required"),
        referral_code: z.string().optional(),
        origin_city: z.string("Origin is required").optional(),
        origin_latitude: z.number("Origin latitude is required").optional(),
        origin_longitude: z.number("Origin longitude is required").optional(),
        terms: z.boolean().refine((value) => value === true, {
            message: "You must accept the terms and conditions",
        }),
    }).refine((data) => data.password === data.password_confirmation, {
        message: "Passwords do not match",
        path: ["password_confirmation"],
    });

export type RegisterFormData = z.infer<typeof registerSchema>;

export const resetPasswordSchema = z
    .object({
        token: z.string().min(1, "Token is required"),
        email: z.string().email("Invalid email address"),
        password: strongPasswordSchema,
        password_confirmation: z
            .string()
            .min(1, "Password confirmation is required"),
    })
    .refine((data) => data.password === data.password_confirmation, {
        message: "Passwords do not match",
        path: ["password_confirmation"],
    });

export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export const completeOAuthSchema = z
    .object({
        first_name: z.string("First name is required").min(1, "First name is required"),
        middle_name: z.string("Middle name is required").optional(),
        last_name: z.string("Last name is required").min(1, "Last name is required"),
        email: z.string("Email is required").email("Please enter a valid email address"),
        phone: z.string("Phone number is required").min(11, "Phone number must be at least 11 characters"),
        role: z.enum(["customer", "business_customer", "driver"]),
        address: z.string("Address is required").min(1, "Address is required"),
        referral_code: z.string().optional(),
        origin_city: z.string("Origin is required").optional(),
        origin_latitude: z.number("Origin latitude is required").optional(),
        origin_longitude: z.number("Origin longitude is required").optional(),
    });

export type CompleteOAuthFormData = z.infer<typeof completeOAuthSchema>;
